import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Phone, MessageCircle, Truck, ChevronRight, Check, Gauge, Fuel, Cog, Users, Palette, Wrench } from "lucide-react";
import Gallery from "@/components/vehicle/Gallery";
import { PrepReport, ServiceTimeline } from "@/components/vehicle/PrepReport";
import FinancePanel from "@/components/vehicle/FinancePanel";
import { EnquiryForm, PartExchangeForm } from "@/components/vehicle/Forms";
import { LabelBadge, StatusBadge } from "@/components/site/VehicleCard";
import { RunningCosts } from "@/components/site/RunningCosts";
import { Reveal } from "@/components/site/Reveal";
import { scrollToId } from "@/components/site/SmoothScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, gbp, miles, STATUS_LABELS } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useSeo } from "@/hooks/useSeo";

const canReserve = (v) => ["available", "just_arrived"].includes(v?.status);

export default function VehicleDetail() {
  const { slug } = useParams();
  const site = useSite();
  const [v, setV] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setV(null); setMissing(false);
    api.get(`/vehicles/${slug}`).then((r) => setV(r?.data || null)).catch(() => setMissing(true));
  }, [slug]);

  const title = v ? `${v.year} ${v.make} ${v.model} ${v.derivative}` : "";
  const origin = window.location.origin;
  useSeo({
    title: v ? v.seo_title || `${title} | Cirencester 4x4` : "Vehicle | Cirencester 4x4",
    description: v?.seo_description,
    path: `/stock/${slug}`,
    image: v?.images?.[0],
    schema: v ? [
      { "@context": "https://schema.org", "@type": "Car", name: title, brand: { "@type": "Brand", name: v.make }, model: v.model,
        vehicleModelDate: String(v.year), color: v.colour, fuelType: v.fuel_type, vehicleTransmission: v.transmission,
        mileageFromOdometer: { "@type": "QuantitativeValue", value: v.mileage, unitCode: "SMI" }, seatingCapacity: v.seats,
        image: (v.images || []).slice(0, 5).map((i) => (i.startsWith("http") ? i : origin + i)), description: v.short_description,
        offers: { "@type": "Offer", price: v.price, priceCurrency: "GBP", availability: v.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock", url: `${origin}/stock/${slug}` } },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Stock", item: origin + "/stock" },
        { "@type": "ListItem", position: 3, name: title, item: `${origin}/stock/${slug}` }] },
    ] : null,
  });

  if (missing) return (
    <main className="mx-auto max-w-3xl px-5 pb-32 pt-40 text-center" data-testid="vehicle-not-found">
      <h1 className="font-serif text-5xl">Vehicle not found</h1>
      <p className="mt-4 text-charcoal/80">This vehicle may have been removed. Please browse our current stock.</p>
      <Link to="/stock" className="btn btn-primary mt-8">View Stock</Link>
    </main>
  );
  if (!v) return <main className="mx-auto max-w-[1440px] px-5 pt-32"><div className="aspect-[3/2] max-w-4xl animate-pulse bg-stone/60" /></main>;

  const sold = v.status === "sold";
  const allDetails = [[Gauge, "Mileage", miles(v.mileage)], [Fuel, "Fuel", v.fuel_type], [Cog, "Transmission", v.transmission],
    [Wrench, "Engine", [v.engine_size, v.power].filter(Boolean).join(" · ")], [Users, "Seats", v.seats], [Palette, "Colour", v.colour]];
  const keyDetails = sold ? allDetails.slice(0, 1) : allDetails;

  return (
    <main className="pb-24 pt-24 lg:pt-28" data-testid="vehicle-detail-page">
      <nav aria-label="Breadcrumb" className="mx-auto max-w-[1440px] px-5 py-4 text-xs text-charcoal/75 sm:px-8 lg:px-12">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="link-u">Home</Link></li><ChevronRight className="h-3 w-3" />
          <li><Link to="/stock" className="link-u">Stock</Link></li><ChevronRight className="h-3 w-3" />
          <li aria-current="page" className="text-charcoal">{v.make} {v.model}</li>
        </ol>
      </nav>

      <section className="mx-auto grid max-w-[1440px] gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="min-w-0 lg:col-span-8"><Gallery images={v.images} alt={title} videoUrl={v.video_url} viewerUrl={v.viewer_360_url} /></div>
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28" data-testid="vehicle-summary">
            <div className="flex flex-wrap gap-2"><StatusBadge status={v.status} /><LabelBadge label={v.badge} className="border border-forest/30" /></div>
            <p className="eyebrow mt-4 text-charcoal/75">{v.year} · {v.make}</p>
            <h1 className="mt-2 font-serif text-4xl leading-[1.02] text-charcoal sm:text-5xl">{v.model}</h1>
            <p className="mt-2 text-base text-charcoal/80">{v.derivative}</p>
            <div className="mt-6 flex items-end gap-4 border-y border-stone py-5">
              <p className="font-serif text-5xl text-forest" data-testid="vehicle-price">{v.status === "sold" && v.price == null ? "Sold" : gbp(v.price)}</p>
              {v.previous_price > v.price && <p className="pb-1.5 text-sm text-charcoal/70 line-through">{gbp(v.previous_price)}</p>}
            </div>
            {site.finance_enabled && v.finance_monthly && v.status !== "sold" && <p className="mt-3 text-sm text-charcoal/80">Finance from <strong>{gbp(v.finance_monthly)}</strong> per month*</p>}
            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
              {keyDetails.map(([Icon, k, val]) => (
                <div key={k} className="flex gap-3">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-forest" strokeWidth={1.5} />
                  <div><dt className="text-[0.68rem] uppercase tracking-[0.14em] text-charcoal/70">{k}</dt><dd className="text-sm font-medium">{val || "—"}</dd></div>
                </div>
              ))}
            </dl>
            <RunningCosts v={v} />
            {v.status !== "sold" ? (
              <div className="mt-8 space-y-3">
                <button onClick={() => scrollToId("enquire")} className="btn btn-primary w-full" data-testid="cta-enquire">Enquire about this vehicle</button>
                {canReserve(v) && <Link to={`/reserve/${v.slug}`} className="btn btn-secondary w-full" data-testid="cta-reserve">Reserve this vehicle</Link>}
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => scrollToId("part-exchange")} className="btn btn-secondary !px-3 !text-[0.7rem]" data-testid="cta-px">Value my part exchange</button>
                  <a href={`tel:${site.phone_href}`} className="btn btn-secondary !px-3 !text-[0.7rem]" data-testid="cta-call"><Phone className="h-3.5 w-3.5" /> Call us</a>
                </div>
                {site.whatsapp && <a href={`https://wa.me/${site.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Enquiry: ${title}`)}`} target="_blank" rel="noreferrer" className="link-u mt-1 inline-flex items-center gap-2 text-sm text-charcoal/80" data-testid="cta-whatsapp"><MessageCircle className="h-4 w-4" /> Message us on WhatsApp</a>}
              </div>
            ) : (
              <div className="mt-8 border border-stone p-5" data-testid="sold-note">
                <p className="font-serif text-2xl text-charcoal">Looking for something similar?</p>
                <Link to="/stock" className="link-u mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-forest" data-testid="sold-similar-link">View current stock <ChevronRight className="h-4 w-4" /></Link>
              </div>
            )}
            {!sold && <p className="mt-6 flex items-center gap-2 text-xs text-charcoal/70"><Truck className="h-4 w-4" /> Nationwide delivery available</p>}
          </div>
        </aside>
      </section>

      {sold ? (
        v.short_description && (
          <section className="mx-auto mt-20 max-w-[1440px] px-5 sm:px-8 lg:px-12" data-testid="sold-short-description">
            <p className="eyebrow text-forest">About this vehicle</p>
            <p className="mt-4 max-w-3xl font-serif text-3xl leading-snug text-charcoal sm:text-4xl">{v.short_description}</p>
          </section>
        )
      ) : (<>
      <section className="mx-auto mt-24 grid max-w-[1440px] gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <Reveal className="lg:col-span-4"><p className="eyebrow text-forest">About this vehicle</p><h2 className="mt-4 font-serif text-4xl text-charcoal">{v.short_description || "An introduction"}</h2></Reveal>
        <Reveal className="prose-c4 text-base text-charcoal/85 lg:col-span-7 lg:col-start-6" delay={0.1}>
          {(v.full_description || "").split("\n").filter(Boolean).map((p, i) => <p key={i} className={i === 0 ? "font-serif text-2xl leading-snug text-forest" : ""}>{p}</p>)}
          {v.cosmetic_notes && <p className="border-l-2 border-stone-dark pl-4 text-sm"><strong>Cosmetic notes:</strong> {v.cosmetic_notes}</p>}
        </Reveal>
      </section>

      <div className="mx-auto mt-24 max-w-[1440px] px-5 sm:px-8 lg:px-12"><PrepReport items={v.preparation_items} /></div>

      {v.key_features?.length > 0 && (
        <section className="mx-auto mt-24 max-w-[1440px] px-5 sm:px-8 lg:px-12" data-testid="key-features">
          <p className="eyebrow text-forest">Key features</p>
          <ul className="mt-8 grid grid-cols-1 border-l border-t border-stone sm:grid-cols-3 lg:grid-cols-5">
            {v.key_features.map((k) => <li key={k} className="flex items-center gap-3 border-b border-r border-stone px-4 py-3.5 font-serif text-lg leading-snug text-charcoal sm:p-5 sm:text-xl"><Check className="h-4 w-4 shrink-0 text-forest" />{k}</li>)}
          </ul>
        </section>
      )}

      <section className="mx-auto mt-24 grid max-w-[1440px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-6">
          <h2 className="font-serif text-4xl text-charcoal">Service history</h2>
          <p className="mb-10 mt-2 text-sm text-charcoal/75">{v.service_history_type}</p>
          <ServiceTimeline entries={v.service_history} />
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <h2 className="font-serif text-4xl text-charcoal">Ownership &amp; provenance</h2>
          <dl className="mt-10 divide-y divide-stone border-y border-stone text-sm" data-testid="ownership-info">
            {[["Keys", v.keys], ["Service history", v.service_history_type], ["MOT expiry", v.mot_expiry && new Date(v.mot_expiry).toLocaleDateString("en-GB", { month: "long", year: "numeric" })], ["Warranty", v.warranty], ["HPI / provenance", v.hpi_status]].map(([k, val]) => (
              <div key={k} className="flex justify-between gap-6 py-4"><dt className="text-charcoal/75">{k}</dt><dd className="text-right font-medium">{val || "—"}</dd></div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-[1440px] px-5 sm:px-8 lg:px-12" data-testid="full-spec">
        <h2 className="font-serif text-4xl text-charcoal">Full specification</h2>
        <Accordion type="multiple" defaultValue={["tech"]} className="mt-8 border-t border-stone">
          <AccordionItem value="tech" className="border-stone">
            <AccordionTrigger className="font-serif text-2xl hover:no-underline" data-testid="spec-tech">Technical details</AccordionTrigger>
            <AccordionContent>
              <dl className="grid gap-x-12 sm:grid-cols-2 lg:grid-cols-3">
                {[["Generation", v.generation], ["Body", v.body_style], ["Doors", v.doors], ["Drive", v.drive_type], ["Interior", v.interior_colour], ["Towing capacity", v.towing_capacity && `${v.towing_capacity.toLocaleString("en-GB")} kg`], ["Kerb weight", v.kerb_weight], ["CO2", v.co2], ["MPG (combined)", v.mpg], ["Road tax", v.road_tax], ["Emissions", v.euro_standard], ["ULEZ", v.ulez_status]].map(([k, val]) => (
                  <div key={k} className="flex justify-between border-b border-stone/70 py-3 text-sm"><dt className="text-charcoal/75">{k}</dt><dd className="font-medium">{val || "—"}</dd></div>
                ))}
              </dl>
            </AccordionContent>
          </AccordionItem>
          {[["opts", "Factory options", v.factory_options], ["std", "Standard equipment", v.standard_equipment]].map(([key, label, list]) => list?.length > 0 && (
            <AccordionItem key={key} value={key} className="border-stone">
              <AccordionTrigger className="font-serif text-2xl hover:no-underline" data-testid={`spec-${key}`}>{label}</AccordionTrigger>
              <AccordionContent><ul className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">{list.map((x) => <li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-forest" />{x}</li>)}</ul></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

          {site.finance_enabled && <section className="mx-auto mt-24 max-w-[1440px] px-5 sm:px-8 lg:px-12"><FinancePanel price={v.price} monthly={v.finance_monthly} /></section>}
          <section className="mx-auto mt-24 grid max-w-[1440px] gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:px-12">
            <div id="enquire" className="scroll-mt-28">
              <p className="eyebrow text-forest">Enquire</p>
              <h2 className="mb-8 mt-3 font-serif text-4xl text-charcoal">Ask us about this vehicle</h2>
              <EnquiryForm vehicle={v} />
            </div>
            <div id="part-exchange" className="scroll-mt-28">
              <p className="eyebrow text-forest">Part exchange</p>
              <h2 className="mb-8 mt-3 font-serif text-4xl text-charcoal">Have a vehicle to part exchange?</h2>
              <PartExchangeForm vehicle={v} />
            </div>
          </section>
          <section className="mx-auto mt-24 max-w-[1440px] px-5 sm:px-8 lg:px-12" data-testid="delivery">
            <div className="flex flex-col gap-6 border-y border-stone py-10 sm:flex-row sm:items-center">
              <Truck className="h-10 w-10 shrink-0 text-forest" strokeWidth={1} />
              <div><h2 className="font-serif text-3xl">Nationwide delivery available</h2><p className="mt-1 text-charcoal/80">Collection from Cirencester is always welcome. We can also arrange delivery throughout mainland UK.</p></div>
            </div>
          </section>
          <MobileBar site={site} v={v} />
      </>)}
    </main>
  );
}

function MobileBar({ site, v }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 border-t border-stone bg-paper/95 backdrop-blur lg:hidden" data-testid="mobile-sticky-bar">
      <a href={`tel:${site.phone_href}`} className="flex items-center justify-center gap-2 py-4 text-xs font-semibold uppercase tracking-[0.12em]" data-testid="sticky-call"><Phone className="h-4 w-4" />Call</a>
      <button onClick={() => scrollToId("enquire")} className="bg-forest py-4 text-xs font-semibold uppercase tracking-[0.12em] text-paper" data-testid="sticky-enquire">Enquire</button>
      {canReserve(v)
        ? <Link to={`/reserve/${v.slug}`} className="flex items-center justify-center py-4 text-xs font-semibold uppercase tracking-[0.12em]" data-testid="sticky-reserve">Reserve</Link>
        : <span className="flex items-center justify-center py-4 text-xs uppercase tracking-[0.12em] text-charcoal/60">{STATUS_LABELS[v.status] || "Reserved"}</span>}
    </div>
  );
}
